/**
 * Reading a tenant's credit balances, and granting it more.
 *
 * TWO THINGS ABOUT THE TOP-UP, both of which are properties of the contract in
 * `@wend/shared` rather than of this file, and both of which this file exists to
 * avoid weakening:
 *
 * 1. **A top-up is a ledger movement, never a balance update.** There is no
 *    function here that sets a balance, and there is no shape in
 *    `@wend/shared` that could express one — `tenantTopupRequestSchema` says how
 *    much to *move* and has no field for what the result should be. The balance
 *    that comes back is the cache as it stands after the movement, read out of
 *    the same transaction that appended the row. Writing `credit_balances`
 *    directly would produce a number with no history behind it, and "where did
 *    this come from" is the first question anybody asks about a balance.
 *
 * 2. **The idempotency token is minted by the caller, once per top-up intent.**
 *    `topupIdempotencyKey` composes it into `topup:<tenant_id>:<request_token>`
 *    server-side; this client sends the token half and nothing else. The reason
 *    it is not derived from the request content is spelled out in the contract:
 *    a content hash collapses two genuinely separate top-ups of the same amount
 *    into one, and a time-bucketed hash stops deduplicating exactly when a retry
 *    crosses a bucket boundary — which is when retries happen, because the thing
 *    that caused them was slow. Only the caller knows that two HTTP requests
 *    were one intent, so the caller says so. {@link TenantTopupPanel} mints a
 *    uuid when the form opens and sends the same one on every submit of that
 *    form.
 *
 * WHAT IS DELIBERATELY ABSENT: any way to *deduct* credits. `topupAmountSchema`
 * is `.int().min(1)`, so a negative grant is unrepresentable rather than
 * refused, and a correction to a wrong top-up is an `adjustment` — a different
 * operation, on a different route, that this slice does not build. The hole is
 * marked here rather than papered over with a signed amount, because a signed
 * amount is one keystroke away from being a deduction nobody designed.
 */
import {
  creditBalancesResponseSchema,
  tenantTopupRequestSchema,
  tenantTopupResponseSchema,
  type CreditBalancesResponse,
  type TenantTopupRequest,
  type TenantTopupResponse,
} from '@wend/shared';

import { tenantBalancesPath, tenantTopupPath } from './admin-endpoints';
import { apiClient } from './api-client';

/**
 * One row per channel, always present — a channel that has never had a credit
 * reads as zero rather than going unlisted. That distinction matters on this
 * screen more than on the tenant's own: `updatedAt === null` is "this workspace
 * has never had a credit on this channel", which is a different sentence from
 * "the balance is zero", and only one of them is ever a reason to be surprised.
 */
export async function fetchTenantBalances(
  tenantId: string,
  signal?: AbortSignal,
): Promise<CreditBalancesResponse> {
  return apiClient.request({
    path: tenantBalancesPath(tenantId),
    schema: creditBalancesResponseSchema,
    ...(signal === undefined ? {} : { signal }),
  });
}

/**
 * Appends a `grant` to this tenant's credit ledger.
 *
 * The response's `applied` is the field that makes "replaying it moves nothing"
 * observable: `true` means this call inserted the row, `false` means the key
 * already existed and this call moved nothing at all — the `ledgerEntryId` and
 * the balance handed back are the ones the *original* top-up produced. A caller
 * that ignores `applied` cannot tell those apart, and would report a second
 * grant that never happened.
 */
export async function topUpTenantCredits(
  tenantId: string,
  input: TenantTopupRequest,
): Promise<TenantTopupResponse> {
  return apiClient.request({
    path: tenantTopupPath(tenantId),
    method: 'POST',
    // Parsed again here so a request this app assembled wrongly — a zero
    // amount, a reason of spaces, a token that is not a uuid — fails in this
    // app, before it reaches the one connection that is BYPASSRLS.
    body: tenantTopupRequestSchema.parse(input),
    schema: tenantTopupResponseSchema,
  });
}
